import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Chip, Tooltip, CircularProgress, Alert, Snackbar, Divider, Accordion,
  AccordionSummary, AccordionDetails, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getArancelDisplayPrice } from '../utils/finance';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;
// Especies para valores de referencia (incluye variantes cachorro)
const REF_SPECIES = [
  { key: 'perro', label: 'Perro' },
  { key: 'perroCachorro', label: 'Perro cachorro' },
  { key: 'gato', label: 'Gato' },
  { key: 'gatoCachorro', label: 'Gato cachorro' },
  { key: 'equino', label: 'Equino' },
  { key: 'bovino', label: 'Bovino' },
];
const CATEGORIES = ['Hematología', 'Bioquímica', 'Urianálisis', 'Parasitología', 'Microbiología', 'Endocrinología', 'Gases', 'Histopatología', 'Metabólicos', 'Inmunología y Serología', 'Biología Molecular', 'Coagulación', 'Otro'];
const PARAM_CATS = ['Serie Roja', 'Serie Blanca', 'Plaquetas', 'Diferencial leucocitario', 'Morfología', 'Función Hepática', 'Función Renal', 'Electrolitos', 'Gases', 'Lípidos', 'Enzimas', 'Metabólicos', 'Perfil proteico', 'Análisis físico-químico', 'Sedimento Urinario', 'General'];
// Subcategorías descriptivas: el resultado es texto libre, sin rangos de referencia
const TEXT_PARAM_CATS = ['Morfología'];
const isTextParam = (p) => p?.valueType === 'text' || TEXT_PARAM_CATS.includes(p?.category);

// Tipos de referencia soportados para parámetros numéricos
const REF_TYPES = [
  { key: 'minmax',  label: 'Mín. y Máx.' },
  { key: 'ranges',  label: 'Normal / Bajo / Alto' },
  { key: 'binary',  label: 'Binario (Positivo / Negativo)' },
  { key: 'none',    label: 'Sin referencia' },
];

// parseFloat tolerante: acepta coma decimal, conserva el 0 y descarta no numéricos
const parseRefBound = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(',', '.');
  if (s === '') return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};
const INSUMO_UNITS = ['mL', 'L', 'mg', 'g', 'µL', 'UI', 'unidad', 'tira', 'tubo', 'lanceta', 'otro'];
const INSUMO_TYPES = ['Reactivo', 'Insumo', 'Consumible', 'Equipo/Kit'];

const newInsumo = () => ({
  id: `i_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  name: '',
  type: 'Reactivo',
  quantity: '',
  unit: 'mL',
  costo: '',
});

const fmt = (n) => Number(n || 0).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const newParam = () => ({
  id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  originName: '',
  unit: '',
  metodo: '',
  category: 'General',
  refType: 'minmax',
  refRanges: {
    perro: { min: '', max: '' },
    perroCachorro: { min: '', max: '' },
    gato: { min: '', max: '' },
    gatoCachorro: { min: '', max: '' },
    default: { min: '', max: '' },
  },
});

const LAB_TIPO_PRESTACION_ID = 'THl2ko6L67kTkUtkfDVU';

const emptyExam = () => ({
  name: '',
  category: 'Hematología',
  tipoMuestra: '',
  description: '',
  arancelId: '',
  arancelPrestacion: '',
  precioNeto: 0,
  active: true,
  produccionInterna: true,
  proveedor: '',
  parameters: [],
  insumos: [],
});

export default function ExamCatalog() {
  const { filterByBranch } = useBranch();
  const [examTypes, setExamTypes] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, exam: null });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadExams = async () => {
    const snap = await getDocs(query(collection(db, 'examTypes'), orderBy('category')));
    setExamTypes(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    setLoading(false);
  };

  useEffect(() => {
    loadExams();
    getDocs(query(
      collection(db, 'aranceles'),
      where('tipoPrestacionId', '==', LAB_TIPO_PRESTACION_ID),
    )).then((snap) => {
      const list = filterByBranch(snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((a) => a.activo !== false), { includeUnassigned: true })
        .sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es'));
      setAranceles(list);
    });
  }, []);

  const openNew = () => setDialog({ open: true, exam: emptyExam() });
  const openEdit = (exam) => setDialog({
    open: true,
    exam: {
      ...exam,
      tipoMuestra: exam.tipoMuestra || '',
      arancelId: exam.arancelId || '',
      arancelPrestacion: exam.arancelPrestacion || '',
      precioNeto: exam.precioNeto ?? 0,
      produccionInterna: exam.produccionInterna !== false,
      proveedor: exam.proveedor || '',
      parameters: (exam.parameters || []).map((p) => {
        const refType = p.refType || 'minmax';
        const refRanges = {};
        REF_SPECIES.forEach(({ key }) => {
          const r = p.refRanges?.[key] || {};
          if (refType === 'ranges') {
            refRanges[key] = {
              low:    { min: r.low?.min ?? '', max: r.low?.max ?? '' },
              normal: { min: r.normal?.min ?? '', max: r.normal?.max ?? '' },
              high:   { min: r.high?.min ?? '', max: r.high?.max ?? '' },
            };
          } else {
            refRanges[key] = { min: r.min ?? '', max: r.max ?? '' };
          }
        });
        return { metodo: '', refType, ...p, refRanges };
      }),
      insumos: (exam.insumos || []).map((i) => ({ ...i })),
    },
  });

  const closeDialog = () => setDialog({ open: false, exam: null });

  const setExamField = (field, value) =>
    setDialog((d) => ({ ...d, exam: { ...d.exam, [field]: value } }));

  const addParameter = () =>
    setDialog((d) => ({ ...d, exam: { ...d.exam, parameters: [...(d.exam.parameters || []), newParam()] } }));

  const addInsumo = () =>
    setDialog((d) => ({ ...d, exam: { ...d.exam, insumos: [...(d.exam.insumos || []), newInsumo()] } }));

  const removeInsumo = (iid) =>
    setDialog((d) => ({ ...d, exam: { ...d.exam, insumos: d.exam.insumos.filter((i) => i.id !== iid) } }));

  const setInsumoField = (iid, field, value) =>
    setDialog((d) => ({
      ...d,
      exam: {
        ...d.exam,
        insumos: d.exam.insumos.map((i) => i.id !== iid ? i : { ...i, [field]: value }),
      },
    }));

  const removeParameter = (pid) =>
    setDialog((d) => ({ ...d, exam: { ...d.exam, parameters: d.exam.parameters.filter((p) => p.id !== pid) } }));

  const setParamField = (pid, field, value) =>
    setDialog((d) => ({
      ...d,
      exam: {
        ...d.exam,
        parameters: d.exam.parameters.map((p) => p.id !== pid ? p : { ...p, [field]: value }),
      },
    }));

  const setParamRange = (pid, species, path, value) =>
    setDialog((d) => ({
      ...d,
      exam: {
        ...d.exam,
        parameters: d.exam.parameters.map((p) => {
          if (p.id !== pid) return p;
          const parts = Array.isArray(path) ? path : [path];
          const r = { ...(p.refRanges?.[species] || {}) };
          let cur = r;
          parts.forEach((part, idx) => {
            if (idx === parts.length - 1) {
              cur[part] = value;
            } else {
              cur[part] = { ...(cur[part] || {}) };
              cur = cur[part];
            }
          });
          return { ...p, refRanges: { ...(p.refRanges || {}), [species]: r } };
        }),
      },
    }));

  const handleSave = async () => {
    const exam = dialog.exam;
    if (!exam.name.trim()) return setSnackbar({ open: true, message: 'El nombre del examen es obligatorio.', severity: 'warning' });
    setSaving(true);
    try {
      const cleanParams = (exam.parameters || []).map((p) => {
        // Parámetros descriptivos (ej. Morfología): texto libre, sin rangos ni unidad
        if (isTextParam(p)) {
          return { ...p, valueType: 'text', refType: 'none', unit: '', refRanges: {} };
        }
        const refType = p.refType || 'minmax';
        let cleanRanges = {};
        if (refType === 'minmax') {
          cleanRanges = Object.fromEntries(
            Object.entries(p.refRanges || {}).map(([sp, rng]) => [
              sp,
              { min: parseRefBound(rng?.min), max: parseRefBound(rng?.max) },
            ])
          );
        } else if (refType === 'ranges') {
          cleanRanges = Object.fromEntries(
            Object.entries(p.refRanges || {}).map(([sp, rng]) => [
              sp,
              {
                low:    { min: parseRefBound(rng?.low?.min),    max: parseRefBound(rng?.low?.max) },
                normal: { min: parseRefBound(rng?.normal?.min), max: parseRefBound(rng?.normal?.max) },
                high:   { min: parseRefBound(rng?.high?.min),   max: parseRefBound(rng?.high?.max) },
              },
            ])
          );
        } else {
          // binary / none
          cleanRanges = {};
        }
        return { ...p, valueType: 'number', refType, refRanges: cleanRanges };
      });
      const cleanInsumos = (exam.insumos || []).map((i) => ({
        ...i,
        quantity: parseFloat(String(i.quantity).replace(',', '.')) || 0,
        costo: parseFloat(String(i.costo).replace(',', '.')) || 0,
      }));
      const data = {
        ...exam,
        parameters: cleanParams,
        insumos: cleanInsumos,
        precioNeto: parseFloat(String(exam.precioNeto).replace(',', '.')) || 0,
        updatedAt: serverTimestamp(),
      };
      if (exam.id) {
        const { id, ...upd } = data;
        await updateDoc(doc(db, 'examTypes', exam.id), upd);
      } else {
        await addDoc(collection(db, 'examTypes'), { ...data, createdAt: serverTimestamp() });
      }
      setSnackbar({ open: true, message: `Examen ${exam.id ? 'actualizado' : 'creado'} correctamente.`, severity: 'success' });
      closeDialog();
      loadExams();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tipo de examen?')) return;
    await deleteDoc(doc(db, 'examTypes', id));
    setSnackbar({ open: true, message: 'Examen eliminado.', severity: 'info' });
    loadExams();
  };

  const grouped = examTypes.reduce((acc, e) => {
    const cat = e.category || 'Otro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(e);
    return acc;
  }, {});

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScienceIcon sx={{ color: LAB_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Catálogo de Exámenes</Typography>
            <Typography variant="caption" color="text.secondary">{examTypes.length} tipos configurados</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: LAB_PRIMARY, fontWeight: 700, borderRadius: 2 }}>
          Nuevo Tipo de Examen
        </Button>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : examTypes.length === 0 ? (
        <Alert severity="info">No hay tipos de examen configurados. Crea el primero.</Alert>
      ) : (
        Object.entries(grouped).map(([cat, exams]) => (
          <Accordion key={cat} defaultExpanded elevation={0}
            sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #E2E8F0', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: LAB_PRIMARY, borderRadius: '12px 12px 0 0', '& .MuiAccordionSummary-expandIconWrapper': { color: '#fff' } }}>
              <Typography variant="subtitle2" fontWeight={700} color="#fff">{cat}</Typography>
              <Chip label={exams.length} size="small"
                sx={{ ml: 1.5, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', height: 20, fontSize: '0.7rem' }} />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Nombre</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="center">Parámetros</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="center">Insumos</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="right">Precio Neto</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="center">Estado</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }} align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exams.map((e) => (
                      <TableRow key={e.id} sx={{ '&:hover': { bgcolor: '#F0F9FF' } }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: LAB_PRIMARY }}>{e.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 220 }}>
                          <Typography noWrap variant="body2" sx={{ fontSize: '0.78rem' }}>{e.description || '—'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={e.parameters?.length || 0} size="small"
                            sx={{ bgcolor: LAB_TEAL + '22', color: LAB_TEAL, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={e.insumos?.length || 0} size="small"
                            sx={{ bgcolor: '#FFF7ED', color: '#C2410C', fontWeight: 700 }} />
                        </TableCell>
                        <TableCell align="right">
                          {e.arancelPrestacion ? (
                            <Box>
                              <Typography variant="body2" fontWeight={700} color={LAB_PRIMARY} sx={{ fontSize: '0.78rem' }} noWrap>
                                {e.arancelPrestacion}
                              </Typography>
                              <Typography variant="caption" color={LAB_TEAL} fontWeight={700}>
                                ${Number(e.precioNeto || 0).toLocaleString('es-CL')}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Sin vincular</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={e.active !== false ? 'Activo' : 'Inactivo'} size="small"
                            sx={{ bgcolor: e.active !== false ? '#DCFCE7' : '#FEE2E2',
                              color: e.active !== false ? '#16A34A' : '#DC2626', fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEdit(e)} sx={{ color: LAB_PRIMARY }}>
                              <EditIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => handleDelete(e.id)} sx={{ color: '#DC2626' }}>
                              <DeleteIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
      {/* Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: LAB_PRIMARY, color: '#fff', fontWeight: 700 }}>
          {dialog.exam?.id ? 'Editar Tipo de Examen' : 'Nuevo Tipo de Examen'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {dialog.exam && (
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 5
                }}>
                <TextField fullWidth size="small" label="Nombre del examen *"
                  value={dialog.exam.name} onChange={(e) => setExamField('name', e.target.value)} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField fullWidth select size="small" label="Categoría *"
                  value={dialog.exam.category} onChange={(e) => setExamField('category', e.target.value)}>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField fullWidth select size="small" label="Estado"
                  value={dialog.exam.active !== false ? 'true' : 'false'}
                  onChange={(e) => setExamField('active', e.target.value === 'true')}>
                  <MenuItem value="true">Activo</MenuItem>
                  <MenuItem value="false">Inactivo</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField fullWidth select size="small" label="Producción interna"
                  value={dialog.exam.produccionInterna !== false ? 'true' : 'false'}
                  onChange={(e) => setExamField('produccionInterna', e.target.value === 'true')}>
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 8
                }}>
                <TextField fullWidth size="small" label="Proveedor"
                  value={dialog.exam.proveedor}
                  onChange={(e) => setExamField('proveedor', e.target.value)}
                  disabled={dialog.exam.produccionInterna !== false}
                  placeholder="Nombre del proveedor externo..."
                  helperText={dialog.exam.produccionInterna !== false ? 'Solo visible si producción interna es No' : ''} />
              </Grid>

              {/* Vincular arancel */}
              <Grid
                size={{
                  xs: 12,
                  sm: 8
                }}>
                <Autocomplete
                  options={aranceles}
                  getOptionLabel={(a) => a.prestacion || ''}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={aranceles.find((a) => a.id === dialog.exam.arancelId) || null}
                  onChange={(_, selected) => {
                    const displayPrice = getArancelDisplayPrice(selected);
                    setDialog((d) => ({
                      ...d,
                      exam: {
                        ...d.exam,
                        arancelId: selected?.id || '',
                        arancelPrestacion: selected?.prestacion || '',
                        precioArancel: displayPrice,
                        precioNeto: displayPrice ? Math.round(displayPrice / 1.19) : 0,
                      },
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Vincular Arancel"
                      placeholder="Buscar prestación..."
                      helperText={aranceles.length === 0 ? 'No hay aranceles de laboratorio configurados' : ''} />
                  )}
                  noOptionsText="Sin resultados"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField fullWidth size="small" label="Precio Arancel (con IVA)" disabled
                  value={dialog.exam.precioArancel ? `$${fmt(dialog.exam.precioArancel)}` : (dialog.exam.precioNeto ? `$${fmt(dialog.exam.precioNeto)}` : '— Sin vincular')}
                  InputProps={{
                    startAdornment: dialog.exam.precioNeto
                      ? <AttachMoneyIcon sx={{ fontSize: 16, color: LAB_TEAL, mr: 0.5 }} />
                      : null,
                    sx: {
                      bgcolor: '#F8FAFC',
                      color: dialog.exam.precioNeto ? LAB_PRIMARY : 'text.disabled',
                      fontWeight: dialog.exam.precioNeto ? 700 : 400,
                    },
                  }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField fullWidth size="small" label="Tipo de muestra"
                  placeholder="Ej: Sangre con EDTA, Orina, Suero…"
                  value={dialog.exam.tipoMuestra || ''}
                  onChange={(e) => setExamField('tipoMuestra', e.target.value)} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 8
                }}>
                <TextField fullWidth size="small" label="Descripción"
                  value={dialog.exam.description} onChange={(e) => setExamField('description', e.target.value)} />
              </Grid>

              <Grid size={12}>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY}>
                    Parámetros ({dialog.exam.parameters?.length || 0})
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addParameter}
                    sx={{ color: LAB_TEAL, fontWeight: 700 }}>
                    Agregar parámetro
                  </Button>
                </Box>

                {(dialog.exam.parameters || []).map((p) => (
                  <Paper key={p.id} elevation={0} sx={{ p: 2, mb: 1.5, border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: '#FAFAFA' }}>
                    <Grid container spacing={1.5} alignItems="flex-start">
                      <Grid
                        size={{
                          xs: 12,
                          sm: 4
                        }}>
                        <TextField fullWidth size="small" label="Nombre parámetro *"
                          value={p.name} onChange={(e) => setParamField(p.id, 'name', e.target.value)} />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 4
                        }}>
                        <TextField fullWidth size="small" label="Nombre origen (alias fuente)"
                          placeholder="Ej: ERITROCITOS, RBC, Hematíes…"
                          value={p.originName || ''} onChange={(e) => setParamField(p.id, 'originName', e.target.value)}
                          helperText="Nombre exacto como aparece en el resultado original" />
                      </Grid>
                      <Grid
                        size={{
                          xs: 6,
                          sm: 4
                        }}>
                        <TextField fullWidth size="small" label="Unidad"
                          value={p.unit} onChange={(e) => setParamField(p.id, 'unit', e.target.value)}
                          disabled={isTextParam(p)}
                          helperText={isTextParam(p) ? 'No aplica en parámetros descriptivos' : ''} />
                      </Grid>
                      <Grid
                        size={{
                          xs: 6,
                          sm: 4
                        }}>
                        <TextField fullWidth select size="small" label="Subcategoría"
                          value={p.category || 'General'}
                          onChange={(e) => setParamField(p.id, 'category', e.target.value)}>
                          {PARAM_CATS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 5
                        }}>
                        <TextField fullWidth size="small" label="Método"
                          placeholder="Ej: Colorimétrico, Tira reactiva, Microscopía…"
                          value={p.metodo || ''} onChange={(e) => setParamField(p.id, 'metodo', e.target.value)} />
                      </Grid>
                      <Grid
                        sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                        size={{
                          xs: 12,
                          sm: 3
                        }}>
                        <Button size="small" color="error" startIcon={<DeleteIcon />}
                          onClick={() => removeParameter(p.id)}>
                          Eliminar
                        </Button>
                      </Grid>

                      {/* Parámetro descriptivo: sin rangos, se informa como texto libre */}
                      {isTextParam(p) ? (
                        <Grid size={12}>
                          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            Parámetro descriptivo ({p.category}): no usa valores de referencia. Al informar se
                            completará con un texto libre describiendo la morfología.
                          </Alert>
                          <TextField fullWidth size="small" multiline rows={2} sx={{ mt: 1.5 }}
                            label="Texto sugerido / plantilla (opcional)"
                            placeholder="Ej: Eritrocitos normocíticos normocrómicos, sin inclusiones…"
                            value={p.defaultText || ''}
                            onChange={(e) => setParamField(p.id, 'defaultText', e.target.value)}
                            helperText="Se precargará como punto de partida al redactar el informe" />
                        </Grid>
                      ) : (
                        <Grid size={12}>
                          <TextField fullWidth select size="small" label="Tipo de referencia"
                            value={p.refType || 'minmax'}
                            onChange={(e) => setParamField(p.id, 'refType', e.target.value)}
                            sx={{ mb: 1.5 }}>
                            {REF_TYPES.map((rt) => (
                              <MenuItem key={rt.key} value={rt.key}>{rt.label}</MenuItem>
                            ))}
                          </TextField>

                          {(p.refType === 'minmax' || !p.refType) && (
                            <Grid container spacing={1}>
                              {REF_SPECIES.map(({ key, label }) => (
                                <Grid key={key} size={{ xs: 6, sm: 4 }}>
                                  <Typography variant="caption" color={LAB_TEAL} fontWeight={700}
                                    sx={{ display: 'block', mb: 0.5 }}>
                                    {label}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <TextField size="small" label="Mín"
                                      value={p.refRanges?.[key]?.min ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, 'min', e.target.value)}
                                      inputProps={{ style: { fontSize: '0.75rem', padding: '4px 6px' } }} />
                                    <TextField size="small" label="Máx"
                                      value={p.refRanges?.[key]?.max ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, 'max', e.target.value)}
                                      inputProps={{ style: { fontSize: '0.75rem', padding: '4px 6px' } }} />
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          )}

                          {p.refType === 'ranges' && (
                            <Grid container spacing={1}>
                              {REF_SPECIES.map(({ key, label }) => (
                                <Grid key={key} size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="caption" color={LAB_TEAL} fontWeight={700}
                                    sx={{ display: 'block', mb: 0.5 }}>
                                    {label}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                    <TextField size="small" label="Bajo mín"
                                      value={p.refRanges?.[key]?.low?.min ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, ['low', 'min'], e.target.value)}
                                      inputProps={{ style: { fontSize: '0.72rem', padding: '4px 4px' } }} />
                                    <TextField size="small" label="Bajo máx"
                                      value={p.refRanges?.[key]?.low?.max ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, ['low', 'max'], e.target.value)}
                                      inputProps={{ style: { fontSize: '0.72rem', padding: '4px 4px' } }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                    <TextField size="small" label="Normal mín"
                                      value={p.refRanges?.[key]?.normal?.min ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, ['normal', 'min'], e.target.value)}
                                      inputProps={{ style: { fontSize: '0.72rem', padding: '4px 4px' } }} />
                                    <TextField size="small" label="Normal máx"
                                      value={p.refRanges?.[key]?.normal?.max ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, ['normal', 'max'], e.target.value)}
                                      inputProps={{ style: { fontSize: '0.72rem', padding: '4px 4px' } }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <TextField size="small" label="Alto mín"
                                      value={p.refRanges?.[key]?.high?.min ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, ['high', 'min'], e.target.value)}
                                      inputProps={{ style: { fontSize: '0.72rem', padding: '4px 4px' } }} />
                                    <TextField size="small" label="Alto máx"
                                      value={p.refRanges?.[key]?.high?.max ?? ''}
                                      onChange={(e) => setParamRange(p.id, key, ['high', 'max'], e.target.value)}
                                      inputProps={{ style: { fontSize: '0.72rem', padding: '4px 4px' } }} />
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          )}

                          {(p.refType === 'binary' || p.refType === 'none') && (
                            <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                              {p.refType === 'binary'
                                ? 'Parámetro binario: al informar se elegirá Positivo o Negativo. No requiere rangos numéricos.'
                                : 'Parámetro sin referencia: no se mostrará rango de referencia ni se calculará estado.'}
                            </Alert>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                ))}

                {dialog.exam.parameters?.length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                    Sin parámetros. Agrega los parámetros del examen con sus rangos de referencia por especie.
                  </Typography>
                )}
              </Grid>

              {/* ── Insumos y Reactivos ── */}
              <Grid size={12}>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InvertColorsIcon sx={{ color: '#C2410C', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY}>
                      Insumos y Reactivos ({dialog.exam.insumos?.length || 0})
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<AddIcon />} onClick={addInsumo}
                    sx={{ color: '#C2410C', fontWeight: 700 }}>
                    Agregar insumo
                  </Button>
                </Box>

                {(dialog.exam.insumos || []).length > 0 ? (
                  <TableContainer component={Paper} elevation={0}
                    sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#FFF7ED' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#92400E' }}>Nombre del insumo / reactivo</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#92400E', width: 120 }}>Tipo</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#92400E', width: 80 }}>Cantidad</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#92400E', width: 80 }}>Unidad</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#92400E', width: 110 }}>Costo ($)</TableCell>
                          <TableCell sx={{ width: 40 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(dialog.exam.insumos || []).map((ins) => (
                          <TableRow key={ins.id} sx={{ '&:hover': { bgcolor: '#FFFBF5' } }}>
                            <TableCell>
                              <TextField fullWidth size="small" placeholder="Ej: Anticoagulante EDTA"
                                value={ins.name}
                                onChange={(e) => setInsumoField(ins.id, 'name', e.target.value)}
                                inputProps={{ style: { fontSize: '0.8rem', padding: '5px 8px' } }} />
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth select size="small"
                                value={ins.type || 'Reactivo'}
                                onChange={(e) => setInsumoField(ins.id, 'type', e.target.value)}
                                inputProps={{ style: { fontSize: '0.8rem' } }}>
                                {INSUMO_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{t}</MenuItem>)}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth size="small" type="number" placeholder="0"
                                value={ins.quantity}
                                onChange={(e) => setInsumoField(ins.id, 'quantity', e.target.value)}
                                inputProps={{ style: { fontSize: '0.8rem', padding: '5px 8px' }, min: 0, step: 0.1 }} />
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth select size="small"
                                value={ins.unit || 'mL'}
                                onChange={(e) => setInsumoField(ins.id, 'unit', e.target.value)}
                                inputProps={{ style: { fontSize: '0.8rem' } }}>
                                {INSUMO_UNITS.map((u) => <MenuItem key={u} value={u} sx={{ fontSize: '0.8rem' }}>{u}</MenuItem>)}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField fullWidth size="small" type="number" placeholder="0"
                                value={ins.costo ?? ''}
                                onChange={(e) => setInsumoField(ins.id, 'costo', e.target.value)}
                                inputProps={{ style: { fontSize: '0.8rem', padding: '5px 8px' }, min: 0 }}
                                InputProps={{ startAdornment: <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 0.3 }}>$</Typography> }} />
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Eliminar">
                                <IconButton size="small" onClick={() => removeInsumo(ins.id)}
                                  sx={{ color: '#DC2626' }}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Fila total */}
                        <TableRow sx={{ bgcolor: '#FEF3C7' }}>
                          <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#92400E', textAlign: 'right', borderTop: '2px solid #FCD34D' }}>
                            Costo total insumos:
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#92400E', borderTop: '2px solid #FCD34D' }}>
                            ${fmt((dialog.exam.insumos || []).reduce((s, i) => s + (parseFloat(i.costo) || 0), 0))}
                          </TableCell>
                          <TableCell sx={{ borderTop: '2px solid #FCD34D' }} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ border: '1px dashed #FED7AA', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#FFFBF5' }}>
                    <Typography variant="caption" color="text.disabled">
                      Sin insumos registrados. Agrega los reactivos y consumibles necesarios para este examen.
                    </Typography>
                  </Box>
                )}

                {/* ── Resumen financiero ── */}
                {(() => {
                  const costoTotal = (dialog.exam.insumos || []).reduce((s, i) => s + (parseFloat(i.costo) || 0), 0);
                  const precio = parseFloat(dialog.exam.precioNeto) || 0;
                  const utilidad = precio - costoTotal;
                  const margen = precio > 0 ? (utilidad / precio) * 100 : null;
                  const margenColor = margen === null ? '#6B7280' : margen >= 50 ? '#16A34A' : margen >= 25 ? '#D97706' : '#DC2626';
                  const margenBg   = margen === null ? '#F3F4F6' : margen >= 50 ? '#DCFCE7'  : margen >= 25 ? '#FEF3C7'  : '#FEE2E2';
                  return (
                    <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC',
                      display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 130 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Costo insumos</Typography>
                        <Typography variant="h6" fontWeight={700} color="#C2410C">${fmt(costoTotal)}</Typography>
                      </Box>
                      <Box sx={{ fontSize: 22, color: '#CBD5E1', fontWeight: 300 }}>+</Box>
                      <Box sx={{ flex: 1, minWidth: 130 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Precio de venta neto</Typography>
                        <Typography variant="h6" fontWeight={700} color={LAB_PRIMARY}>
                          {precio > 0 ? `$${fmt(precio)}` : <span style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>Sin precio</span>}
                        </Typography>
                      </Box>
                      <Box sx={{ fontSize: 22, color: '#CBD5E1', fontWeight: 300 }}>=</Box>
                      <Box sx={{ flex: 1, minWidth: 130 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Utilidad bruta</Typography>
                        <Typography variant="h6" fontWeight={700} color={precio > 0 ? (utilidad >= 0 ? '#16A34A' : '#DC2626') : '#9CA3AF'}>
                          {precio > 0 ? `$${fmt(utilidad)}` : '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: margenBg, minWidth: 110, textAlign: 'center' }}>
                        <Typography variant="caption" color={margenColor} fontWeight={700} display="block">Margen</Typography>
                        <Typography variant="h6" fontWeight={800} color={margenColor}>
                          {margen !== null ? `${margen.toFixed(1)}%` : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: LAB_PRIMARY, fontWeight: 700 }}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
